import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';


export const MAX_LIST_HEIGHT = 288;

export interface Anchor {
  left: number;
  top: number;
  width: number;
  height: number;
  above: boolean;
}

export function useAnchor(open: boolean) {
  const ref = useRef<HTMLInputElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const measure = useCallback(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    setAnchor({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      above: below < Math.min(MAX_LIST_HEIGHT, 200) && rect.top > below,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => measure();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, measure]);

  return { ref, anchor, measure };
}

export function AnchoredList({
  anchor,
  id,
  listRef,
  minWidth = 260,
  children,
}: {
  anchor: Anchor;
  id: string;
  listRef: React.RefObject<HTMLDivElement | null>;
  minWidth?: number;
  children: ReactNode;
}) {
  return createPortal(
    <div
      ref={listRef}
      id={id}
      role="listbox"
      data-noprint="1"
      style={{
        position: 'fixed',
        left: anchor.left,
        width: Math.max(anchor.width, minWidth),
        ...(anchor.above
          ? { bottom: window.innerHeight - anchor.top + 4 }
          : { top: anchor.top + anchor.height + 4 }),
        maxHeight: MAX_LIST_HEIGHT,
        zIndex: 90,
      }}
      className={cn(
        'overflow-y-auto rounded-[var(--radius-sm)] border border-[var(--line)]',
        'bg-[var(--surface)] py-1 shadow-[var(--shadow-2,0_12px_28px_rgba(0,0,0,0.18))]',
      )}
      onMouseDown={(event) => event.preventDefault()}
    >
      {children}
    </div>,
    document.body,
  );
}
